import { lazy, Suspense } from 'react';
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  createBrowserRouter,
  RouterProvider,
} from 'react-router-dom';
import { AppLoader } from '@/components/AppLoader';
import { AuthLayout } from '@/components/auth';
import { Banner } from '@/components/Banner';
import { Auth, Connect, ResetPassword, ResetPasswordRequest } from '@/pages';
import { Launch } from '@/pages/Launch';
import { GlobalComponents } from './components/GlobalComponents';
import { MagicTabsIndex } from './components/MagicTabsIndex';
import { MagicTabsLayout } from './components/MagicTabsLayout';
import { Redirect } from './components/Redirect';
import { RequireAuth } from './components/RequireAuth';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { SuspenseContainer } from './components/SuspenseContainer';

const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Schedule = lazy(() => import('@/pages/Schedule'));
// Lazy: statically importing ScheduleLoader pulled `@/pages/Schedule/contexts`
// into the always-eager entry bundle (it's also imported, dynamically, by the
// lazy Schedule page chunks, so Rollup gives it its own "contexts" chunk —
// but a static importer still forces that chunk to be modulepreloaded on
// every route, including anonymous /page/* visitors who never touch
// Schedule). See Sentry WEB-CLIENT-29Q et al, "Large Render Blocking Asset".
const ScheduleLoader = lazy(() =>
  import('./components/ScheduleLoader').then((m) => ({
    default: m.ScheduleLoader,
  }))
);
// Lazy for the same reason as ScheduleLoader above: SnippetSuspenseFallback
// statically renders a ScheduleContextProvider so a snippet opened from a
// schedule keeps that schedule's chrome behind its own loading state, but a
// static import of it pulls the same `@/pages/Schedule/contexts` module graph
// into this always-eager router bundle — this time for every route in the
// app, not just Schedule ones, since AppRouter itself is never lazy. Wrapped
// in its own Suspense with a plain fallback since it's rendered as *another*
// route's Suspense fallback, where the element itself must be able to render
// immediately (Sentry WEB-CLIENT-2A1 / 2A4 / 2A7, "Large Render Blocking
// Asset").
const SnippetSuspenseFallback = lazy(() =>
  import('./components/SnippetSuspenseFallback').then((m) => ({
    default: m.SnippetSuspenseFallback,
  }))
);
const ScheduleMessages = lazy(
  () => import('@/pages/Schedule/pages/ScheduleMessages')
);
const ScheduleSnippets = lazy(
  () => import('@/pages/Schedule/pages/ScheduleSnippets')
);
const ScheduleSnippet = lazy(
  () => import('@/pages/Schedule/pages/ScheduleSnippet')
);
const ScheduleSources = lazy(
  () => import('@/pages/Schedule/pages/ScheduleSources')
);
const ScheduleEntry = lazy(
  () => import('@/pages/Schedule/pages/ScheduleEntry')
);
const Magic = lazy(() => import('@/features/magic/MagicRoutes'));
const MagicExtension = lazy(() => import('@/features/magic/extension'));
const MagicExtensionAuth = lazy(
  () => import('@/features/magic/extension/pages/MagicExtensionAuth')
);
const MagicAuthSuccess = lazy(
  () => import('@/features/magic/extension/pages/MagicExtensionAuthSuccess')
);
const MagicToPage = lazy(() => import('@/features/magic/pages/MagicPage'));
const MagicToCanva = lazy(() => import('@/features/magic/pages/MagicCanva'));
const EditPageExperience = lazy(
  () => import('@/features/experiences/pages/EditPageExperience')
);
const Schools = lazy(() => import('@/pages/Schools'));
const Templates = lazy(() => import('@/pages/Templates'));
const ViewPageExperience = lazy(
  () => import('@/features/experiences/pages/ViewPageExperience')
);
const PlannerLandingPage = lazy(
  () => import('@/features/experiences/pages/PlannerLandingPage')
);
const DistrictPlannerLandingPage = lazy(
  () => import('@/features/experiences/pages/DistrictPlannerLandingPage')
);
const CreateExperience = lazy(
  () => import('@/features/experiences/pages/CreateExperience')
);

const AppRoutes = () => {
  return (
    <>
      <GlobalComponents />

      <Routes>
        {/* Unauthenticated routes */}
        <Route element={<AuthLayout mt={{ base: 4, sm: 12 }} />} path="/">
          <Route element={<Auth />} path="auth" />
          <Route element={<Redirect path="/auth" />} path="register" />
          <Route element={<Redirect path="/auth" />} path="login" />
          <Route path="reset-password">
            <Route element={<ResetPasswordRequest />} index />
            <Route element={<ResetPassword />} path=":token" />
          </Route>
        </Route>

        {/* Magic & dashboard tabs */}
        <Route element={<MagicTabsLayout />} path="/">
          <Route element={<MagicTabsIndex />} index />
          <Route element={<Magic />} path="magic/*" />
          {/* The school workflow is a tab on /magic, but /magic-schools is the
              link that gets handed out, so it redirects onto that tab. The
              splat keeps a stray nested URL off a blank page; nothing under it
              needs preserving. */}
          <Route
            element={<Navigate replace to="/magic?mode=schools" />}
            path="magic-schools/*"
          />
          <Route element={<MagicToPage />} path="magic-page" />
          <Route element={<Dashboard />} path="dashboard" />
          <Route element={<Schools />} path="internal/schools" />
          <Route element={<Templates />} path="internal/templates" />
        </Route>

        <Route element={<MagicToCanva />} path="magic-canva" />

        {/* Where /oauth/authorize sends someone to approve a connector. The
            decision stays with the authorization server; this is the screen
            that asks. RequireAuth is a backstop: authorize only sends a signed
            in user here, but a stale tab reaching it should sign in and return
            rather than see an empty page. */}
        <Route
          path="/connect"
          element={
            <RequireAuth>
              <Connect />
            </RequireAuth>
          }
        />

        <Route
          path="/launch/auth"
          element={
            <RequireAuth>
              <Launch />
            </RequireAuth>
          }
        />

        <Route
          element={
            <>
              <Banner />
              <SuspenseContainer>
                <Outlet />
              </SuspenseContainer>
            </>
          }
        >
          <Route element={<ScheduleLoader />} path="/schedule">
            <Route element={<Navigate to="/" />} index />
            <Route path=":slug">
              <Route element={<Schedule />} index path="*" />
              <Route element={<ScheduleSources />} path="sources/*" />
              <Route element={<ScheduleSnippets />} path="snippets" />
              <Route path="messages">
                <Route element={<Navigate to="scheduled" />} index />
                <Route element={<ScheduleMessages />} path=":view" />
              </Route>
              <Route path="item">
                <Route element={<Navigate to="../" />} index />
                <Route element={<ScheduleEntry />} path=":entryId" />
                <Route
                  element={<ScheduleEntry />}
                  path=":entryId/:instanceTimestamp"
                />
              </Route>
            </Route>
          </Route>
        </Route>

        {/* Maybe authenticated routes */}
        <Route
          path="/snippet"
          element={
            <SuspenseContainer
              fallback={
                <Suspense fallback={<AppLoader />}>
                  <SnippetSuspenseFallback />
                </Suspense>
              }
            >
              <Outlet />
            </SuspenseContainer>
          }
        >
          <Route element={<Navigate replace to="/" />} index />
          <Route element={<ScheduleSnippet />} path=":snippetId" />
        </Route>

        {/* Magic extension */}
        <Route
          path="/magic-extension"
          element={
            <SuspenseContainer
              fallback={<AppLoader layerStyle="magicBackground" />}
            >
              <MagicExtension />
            </SuspenseContainer>
          }
        />
        <Route
          path="/magic-auth"
          element={
            <SuspenseContainer>
              <Outlet />
            </SuspenseContainer>
          }
        >
          <Route element={<MagicExtensionAuth />} index />
          <Route element={<MagicAuthSuccess />} path="success" />
        </Route>

        <Route
          path="/experience/create"
          element={
            <RequireAuth>
              <SuspenseContainer>
                <CreateExperience />
              </SuspenseContainer>
            </RequireAuth>
          }
        />
        <Route
          path="/experience/:id/edit"
          element={
            <SuspenseContainer>
              <EditPageExperience />
            </SuspenseContainer>
          }
        />
        <Route
          path="/page/:slug"
          element={
            <SuspenseContainer>
              <ViewPageExperience />
            </SuspenseContainer>
          }
        />
        <Route
          path="/page/:slug/planner"
          element={
            <SuspenseContainer>
              <PlannerLandingPage />
            </SuspenseContainer>
          }
        />
        <Route
          path="/page/:slug/district-planner"
          element={
            <SuspenseContainer>
              <DistrictPlannerLandingPage />
            </SuspenseContainer>
          }
        />
      </Routes>
    </>
  );
};

const router = createBrowserRouter([
  { path: '*', element: <AppRoutes />, errorElement: <RouteErrorBoundary /> },
]);

export const AppRouter = () => <RouterProvider router={router} />;
